import type { ProxyOutput } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { useUpdateProxy } from "@/services";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
} from "@st-marketplace/ui";
import React from "react";
import { ProxyForm } from "./ProxyForm";
import type { ProxyFormData } from "./proxyForm.schema";

interface ProxyFormEditProps {
	selectedProxy: ProxyOutput;
	open: boolean;
	onClose: () => void;
}

const ProxyFormEdit: React.FC<ProxyFormEditProps> = ({
	selectedProxy,
	open,
	onClose,
}) => {
	const { t } = useTranslation();
	const updateProxyMutation = useUpdateProxy();

	const initialValues = React.useMemo(
		() => ({
			ip_address: selectedProxy.ip_address,
			port: selectedProxy.port,
			username: selectedProxy.username,
			password: selectedProxy.password,
		}),
		[selectedProxy],
	);

	const handleSubmit = async (data: ProxyFormData) => {
		await updateProxyMutation.mutateAsync({
			path: { proxy_id: selectedProxy.id },
			body: {
				ip_address: data.ip_address,
				port: data.port!,
				username: data.username ?? null,
				password: data.password ?? null,
			},
		});
		onClose();
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="w-full max-w-3xl">
				<DialogHeader>
					<DialogTitle>{t("proxy.edit.formTitle")}</DialogTitle>
				</DialogHeader>
				<DialogScrollableArea className="max-h-[90vh] pt-0 pb-6">
					<ProxyForm
						submitLabel={t("proxy.edit.formSubmit")}
						initialValues={initialValues}
						loading={updateProxyMutation.isPending}
						onSubmit={handleSubmit}
					/>
				</DialogScrollableArea>
			</DialogContent>
		</Dialog>
	);
};

export default ProxyFormEdit;
